import { Fragment, useMemo, useState, type ReactNode } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Button } from '../widgets/Button'
import { contentLines, diffText, hunks, sideText, type DiffLine, type DiffSection, type DiffSeg, type TextDiff } from './textDiff'

/**
 * A long text value's change, read as ONE text (SEN-460; owner direction: Google Docs suggestion mode,
 * Word track changes, GitHub's rich Markdown diff): the new text rendered as markdown with the changes
 * inline — deleted words struck through and muted, inserted words highlighted (tokens, light and dark) —
 * grouped by the text's markdown sections. A section without changes is one line ("«Data sources» ·
 * unchanged") that opens on click; a changed section shows its changes with two lines of context, the
 * rest folded into "N unchanged lines". Nothing is side by side and nothing scrolls sideways, so the phone
 * reads the same view. "Raw text" shows the markdown source with the same marks.
 */

const BOLD = 1
const ITALIC = 2
const CODE = 4
const MARK = 8 // markdown syntax: hidden in the rendered view

type Block = 'p' | 'h' | 'li' | 'ol' | 'quote' | 'code' | 'table' | 'blank'

/** The block a line is (from the side the reader sees) and how many leading characters are its syntax. */
function blockOf(text: string, inFence: boolean): { block: Block; prefix: number; level?: number; num?: string } {
  if (/^\s*```/.test(text)) return { block: 'code', prefix: text.length }
  if (inFence) return { block: 'code', prefix: 0 }
  if (!text.trim()) return { block: 'blank', prefix: 0 }
  let m = /^(#{1,6})\s+/.exec(text)
  if (m) return { block: 'h', prefix: m[0].length, level: m[1]!.length }
  m = /^\s*[-*+]\s+/.exec(text)
  if (m) return { block: 'li', prefix: m[0].length }
  m = /^\s*(\d+)[.)]\s+/.exec(text)
  if (m) return { block: 'ol', prefix: m[0].length, num: `${m[1]}.` }
  m = /^>\s?/.exec(text)
  if (m) return { block: 'quote', prefix: m[0].length }
  if (/^\s*\|/.test(text)) return { block: 'table', prefix: 0 }
  return { block: 'p', prefix: 0 }
}

/** Per character of one side's line: bold / italic / code, and which characters are markdown syntax. */
function marksOf(text: string, prefix: number, block: Block): Uint8Array {
  const f = new Uint8Array(text.length)
  for (let i = 0; i < Math.min(prefix, text.length); i++) f[i] = MARK
  if (block === 'code' || block === 'table') return f
  const re = /(\*\*(?=\S)[^*]+?(?<=\S)\*\*|\*(?=\S)[^*]+?(?<=\S)\*|`[^`]+`)/g
  re.lastIndex = prefix
  let m: RegExpExecArray | null
  while ((m = re.exec(text)) !== null) {
    const tok = m[0]
    const w = tok.startsWith('**') ? 2 : 1
    const style = tok.startsWith('`') ? CODE : w === 2 ? BOLD : ITALIC
    for (let i = 0; i < tok.length; i++) f[m.index + i] = (i < w || i >= tok.length - w) ? MARK : style
  }
  return f
}

const INS = 'rounded-sm bg-success/15 text-foreground underline decoration-success/60 decoration-1 underline-offset-[3px] dark:bg-success/25'
// a deletion right before an insertion keeps a hair of space between them ("~~where it matters~~ using …")
const DEL = 'text-muted-foreground line-through decoration-destructive decoration-[1.5px] [&+ins]:ml-1'

function styled(text: string, flags: number, key: number): ReactNode {
  let node: ReactNode = text
  if (flags & CODE) node = <code className="rounded-sm bg-muted px-1 font-mono text-[0.9em]">{node}</code>
  if (flags & ITALIC) node = <em>{node}</em>
  if (flags & BOLD) node = <strong className="font-semibold">{node}</strong>
  return <Fragment key={key}>{node}</Fragment>
}

/** One merged line's words: each segment split where its formatting changes; syntax hidden unless raw. */
function Words({ segs, oldMarks, newMarks, raw }: { segs: DiffSeg[]; oldMarks: Uint8Array; newMarks: Uint8Array; raw: boolean }) {
  const out: ReactNode[] = []
  let po = 0
  let pn = 0
  let k = 0
  for (const s of segs) {
    const marks = s.op === 'del' ? oldMarks : newMarks
    const start = s.op === 'del' ? po : pn
    const parts: ReactNode[] = []
    let run = ''
    let runFlags = -1
    const flush = () => { if (run) parts.push(styled(run, runFlags, k++)); run = '' }
    for (let i = 0; i < s.text.length; i++) {
      const f = raw ? 0 : (marks[start + i] ?? 0)
      if (f & MARK) continue
      if (f !== runFlags) { flush(); runFlags = f }
      run += s.text[i]
    }
    flush()
    if (s.op !== 'ins') po += s.text.length
    if (s.op !== 'del') pn += s.text.length
    if (!parts.length) continue
    out.push(s.op === 'ins' ? <ins key={k++} data-testid="diff-ins" className={INS}>{parts}</ins>
      : s.op === 'del' ? <del key={k++} data-testid="diff-del" className={DEL}>{parts}</del>
      : <Fragment key={k++}>{parts}</Fragment>)
  }
  return <>{out}</>
}

const HEADING_CLASS: Record<number, string> = {
  1: 'text-base font-semibold', 2: 'text-sm font-semibold', 3: 'text-sm font-semibold', 4: 'text-sm font-medium',
}

/** A merged line rendered as its markdown block (or as source text, raw). */
function Line({ line, fence, raw }: { line: DiffLine; fence: boolean; raw: boolean }) {
  const oldText = sideText(line.segs, 'old')
  const newText = sideText(line.segs, 'new')
  const shown = line.kind === 'del' ? oldText : newText
  const b = blockOf(shown, fence)
  const ob = blockOf(oldText, fence)
  const nb = blockOf(newText, fence)
  const oldMarks = marksOf(oldText, ob.block === b.block ? ob.prefix : 0, ob.block)
  const newMarks = marksOf(newText, nb.block === b.block ? nb.prefix : 0, nb.block)
  const words = <Words segs={line.segs} oldMarks={oldMarks} newMarks={newMarks} raw={raw} />
  const whole = line.kind === 'ins' ? 'ins' : line.kind === 'del' ? 'del' : undefined
  const common = { 'data-testid': 'diff-line', 'data-kind': line.kind } as const
  if (raw) {
    return b.block === 'blank' ? <div {...common} className="h-3" />
      : <div {...common} className="whitespace-pre-wrap break-words font-mono text-xs leading-relaxed">{words}</div>
  }
  switch (b.block) {
    case 'blank': return <div {...common} className="h-2" />
    case 'h': return <div {...common} role="heading" aria-level={Math.min((b.level ?? 2) + 2, 6)}
      className={`pt-1 ${HEADING_CLASS[b.level ?? 2] ?? HEADING_CLASS[4]}`}>{words}</div>
    case 'li': return <div {...common} className="flex gap-2 pl-1"><span aria-hidden className="select-none text-muted-foreground">•</span><span className="min-w-0 flex-1">{words}</span></div>
    case 'ol': return <div {...common} className="flex gap-2 pl-1"><span aria-hidden className={`select-none tabular-nums ${whole === 'del' ? 'text-muted-foreground line-through' : 'text-muted-foreground'}`}>{b.num}</span><span className="min-w-0 flex-1">{words}</span></div>
    case 'quote': return <div {...common} className="border-l-2 border-border pl-3 text-muted-foreground">{words}</div>
    case 'code': return <div {...common} className="whitespace-pre-wrap break-words rounded-sm bg-muted px-2 font-mono text-xs">{words}</div>
    case 'table': return <div {...common} className="whitespace-pre-wrap break-words font-mono text-xs">{words}</div>
    default: return <p {...common}>{words}</p>
  }
}

/** Lines [from, to) of a section, knowing which open a code fence (fence state carried from the top). */
function Lines({ section, from, to, raw }: { section: DiffSection; from: number; to: number; raw: boolean }) {
  // the fence state at `from`: count fence lines above it
  let fence = false
  for (let i = 0; i < from; i++) if (/^\s*```/.test(sideText(section.lines[i]!.segs, 'new'))) fence = !fence
  const out: ReactNode[] = []
  for (let i = from; i < to; i++) {
    const line = section.lines[i]!
    out.push(<Line key={i} line={line} fence={fence} raw={raw} />)
    if (/^\s*```/.test(sideText(line.segs, line.kind === 'del' ? 'old' : 'new'))) fence = !fence
  }
  return <>{out}</>
}

function sectionName(s: DiffSection): string {
  return s.title || 'Opening text'
}

function Counts({ added, removed }: { added: number; removed: number }) {
  return <span className="shrink-0 text-xs tabular-nums" data-testid="text-diff-counts">
    {added > 0 && <span className="text-success">+{added}</span>}
    {added > 0 && removed > 0 && <span className="text-muted-foreground"> / </span>}
    {removed > 0 && <span className="text-destructive">−{removed}</span>}
  </span>
}

/** A changed section: open by default, its changes with context; a gap opens where clicked. */
function ChangedSection({ section, raw }: { section: DiffSection; raw: boolean }) {
  const [open, setOpen] = useState(true)
  const [shown, setShown] = useState<Set<number>>(new Set())
  const runs = useMemo(() => hunks(section, 1), [section])
  // the heading line is in the header; the body starts after it
  const body = section.title ? runs.map((r) => (r.from === 0 ? { ...r, from: 1 } : r)).filter((r) => r.to > r.from) : runs
  return <section data-testid="text-diff-section" data-changed="true" className="min-w-0">
    <button type="button" data-testid="text-diff-section-toggle" aria-expanded={open} onClick={() => setOpen((o) => !o)}
      className="flex w-full min-w-0 items-start gap-1.5 px-3 py-2 text-left hover:bg-accent/60">
      {open ? <ChevronDown aria-hidden className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        : <ChevronRight aria-hidden className="mt-0.5 size-4 shrink-0 text-muted-foreground" />}
      <span className="min-w-0 flex-1 font-semibold">
        {section.oldTitle && <><del className={DEL}>{section.oldTitle}</del>{' '}<ins className={INS}>{sectionName(section)}</ins></>}
        {!section.oldTitle && sectionName(section)}
      </span>
      <Counts added={section.added} removed={section.removed} />
    </button>
    {open && <div className="grid min-w-0 gap-0.5 break-words px-3 pb-3 pl-9 text-sm leading-relaxed" data-testid="text-diff-section-body">
      {body.map((r) => (r.show || shown.has(r.from))
        ? <Lines key={r.from} section={section} from={r.from} to={r.to} raw={raw} />
        : <button key={r.from} type="button" data-testid="text-diff-gap"
            onClick={() => setShown((s) => new Set(s).add(r.from))}
            className="my-1 w-full rounded-md border border-dashed border-border px-2 py-1 text-left text-xs text-muted-foreground hover:bg-accent/60 hover:text-foreground">
            ⋯ {contentLines(section, r.from, r.to)} unchanged {contentLines(section, r.from, r.to) === 1 ? 'line' : 'lines'}
          </button>)}
    </div>}
  </section>
}

/** An unchanged section: one line; opens to its text. */
function UnchangedSection({ section, raw }: { section: DiffSection; raw: boolean }) {
  const [open, setOpen] = useState(false)
  return <section data-testid="text-diff-section" data-changed="false" className="min-w-0">
    <button type="button" data-testid="text-diff-section-toggle" aria-expanded={open} onClick={() => setOpen((o) => !o)}
      className="flex w-full min-w-0 items-center gap-1.5 px-3 py-1.5 text-left text-xs text-muted-foreground hover:bg-accent/60 hover:text-foreground">
      {open ? <ChevronDown aria-hidden className="size-4 shrink-0" /> : <ChevronRight aria-hidden className="size-4 shrink-0" />}
      <span className="min-w-0 flex-1 truncate"><span className="font-medium text-foreground/80">{sectionName(section)}</span> · unchanged</span>
    </button>
    {open && <div className="grid min-w-0 gap-0.5 break-words px-3 pb-3 pl-9 text-sm leading-relaxed">
      <Lines section={section} from={section.title ? 1 : 0} to={section.lines.length} raw={raw} />
    </div>}
  </section>
}

/** "2 of 12 sections changed · +48 / −31 words" */
export function textDiffSummary(d: TextDiff): string {
  if (!d.changed) return 'No change'
  const total = d.sections.length
  const what = total > 1
    ? `${d.changedSections} of ${total} sections changed`
    : 'Changed'
  const words = [d.added ? `+${d.added}` : null, d.removed ? `−${d.removed}` : null].filter(Boolean).join(' / ')
  return `${what} · ${words} words`
}

/** Three or more unchanged sections in a row: one line naming them; open, each is its own line. */
function UnchangedRun({ sections, raw }: { sections: DiffSection[]; raw: boolean }) {
  const [open, setOpen] = useState(false)
  if (open) return <>{sections.map((s, i) => <UnchangedSection key={`${i}-${s.title}`} section={s} raw={raw} />)}</>
  return <button type="button" data-testid="text-diff-unchanged-run" aria-expanded={false} onClick={() => setOpen(true)}
    className="flex w-full min-w-0 items-center gap-1.5 px-3 py-1.5 text-left text-xs text-muted-foreground hover:bg-accent/60 hover:text-foreground">
    <ChevronRight aria-hidden className="size-4 shrink-0" />
    <span className="min-w-0 flex-1 truncate">
      <span className="font-medium text-foreground/80">{sections.length} sections unchanged</span>
      {' · '}{sections.map(sectionName).join(', ')}
    </span>
  </button>
}

/** The sections as shown: each changed one, each unchanged one, and runs of unchanged ones folded. */
function sectionItems(sections: DiffSection[]): Array<DiffSection | DiffSection[]> {
  const out: Array<DiffSection | DiffSection[]> = []
  let run: DiffSection[] = []
  const flush = () => { if (run.length >= 3) out.push(run); else out.push(...run); run = [] }
  for (const s of sections) {
    if (s.changed) { flush(); out.push(s) } else run.push(s)
  }
  flush()
  return out
}

export function TextDiffView({ field, oldText, newText, record, diff, showSummary = true }: {
  field: string
  oldText: string
  newText: string
  /** the record's name, when the card changes several records */
  record?: string | null
  /** the diff, when the card already computed it (its header says the counts) */
  diff?: TextDiff
  /** false when the card's header already says "2 of 12 sections changed · +25 / −3 words" */
  showSummary?: boolean
}) {
  const computed = useMemo(() => diff ?? diffText(oldText, newText), [diff, oldText, newText])
  const d = computed
  const [raw, setRaw] = useState(false)
  return <div data-testid="text-diff" className="min-w-0 overflow-hidden rounded-lg border border-border">
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-border bg-muted/40 py-1 pl-3 pr-1">
      <p className="min-w-0 flex-1 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">{field}</span>
        {record && <> · {record}</>}
        {showSummary && <>{' · '}<span data-testid="text-diff-summary">{textDiffSummary(d)}</span></>}
      </p>
      <Button variant="quiet" data-testid="text-diff-raw-toggle" aria-pressed={raw} onClick={() => setRaw((r) => !r)}>
        {raw ? 'Formatted' : 'Raw text'}
      </Button>
    </div>
    <div className="divide-y divide-border">
      {sectionItems(d.sections).map((item, i) => Array.isArray(item)
        ? <UnchangedRun key={`run-${i}`} sections={item} raw={raw} />
        : item.changed
          ? <ChangedSection key={`${i}-${item.title}`} section={item} raw={raw} />
          : <UnchangedSection key={`${i}-${item.title}`} section={item} raw={raw} />)}
    </div>
  </div>
}
